import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import ContinueActiveItemModal from './ContinueActiveItemModal';
import CloseActiveItemModal from './CloseActiveItemModal';
import ShiftSelectionModal from './ShiftSelectionModal';
import LanguageSelector from '../LanguageSelector';
import '../../styles/global.css';

interface ProductionLine {
  id: number;
  name: string;
  description?: string;
}

const Login: React.FC = () => {
  const { selectedLine, isAuthenticated, isPackaging, isTracker, activeItem, setActiveItem, selectedShift, setSelectedShift } = useAuth();
  const { t } = useLanguage();
  const [loginMode, setLoginMode] = useState<'pin' | 'email'>('pin');
  const [pin, setPin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showContinueModal, setShowContinueModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [availableLines, setAvailableLines] = useState<ProductionLine[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const { login, loginWithPin } = useAuth();
  const navigate = useNavigate();

  // Fetch available production lines
  useEffect(() => {
    const fetchLines = async () => {
      try {
        const response = await api.get('/production-lines/active');
        // Ensure response.data is an array
        const lines = Array.isArray(response.data) ? response.data : [];
        setAvailableLines(lines);
        
        // Use selectedLine from context (which is loaded from cookie/localStorage)
        if (selectedLine) {
          setSelectedLineId(selectedLine.id);
        } else if (lines.length > 0 && !selectedLineId) {
          // Auto-select first line if no line is logged in and no line is selected
          setSelectedLineId(lines[0].id);
        }
      } catch (error) {
        console.error('Error fetching production lines:', error);
        // Set empty array on error to prevent map errors
        setAvailableLines([]);
      }
    };
    
    if (!isAuthenticated && loginMode === 'pin') {
      fetchLines();
    }
  }, [isAuthenticated, loginMode, selectedLine, selectedLineId]);

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get line_id from selectedLine (from context/localStorage) or selectedLineId (from dropdown)
      // Priority: selectedLine (from localStorage/cookie) > selectedLineId (user's choice from dropdown)
      // If line is in localStorage, use it and don't allow change
      const lineIdToUse = selectedLine?.id || selectedLineId;
      
      // Admin can login without line_id, regular users need line_id
      if (!lineIdToUse) {
        // Try to login as admin (without line_id)
        await loginWithPin(pin);
        setLoading(false);
        // Let useEffect handle shift selection check and navigation
        return;
      }
      
      await loginWithPin(pin, lineIdToUse);
      setLoading(false);
      
      // Let useEffect handle shift selection check and navigation
      // useEffect will check selectedShift and activeItem after isAuthenticated becomes true
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.response?.data?.errors?.pin?.[0] || t('auth.invalidPin');
      setError(errorMessage);
      setPin('');
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    setPin(numericValue);
  };


  // Track previous authentication state to detect login
  // Initialize with current isAuthenticated value to avoid false positives on mount
  const prevIsAuthenticatedRef = useRef<boolean>(isAuthenticated);

  // Check for shift selection and active item after login
  useEffect(() => {
    // Update ref on every render to track changes
    const prevValue = prevIsAuthenticatedRef.current;
    const currentValue = isAuthenticated;
    
    // Don't do anything if not authenticated or still loading
    if (!isAuthenticated || loading) {
      prevIsAuthenticatedRef.current = isAuthenticated;
      return;
    }

    // Detect when user just logged in (transition from false to true)
    const justLoggedIn = !prevValue && currentValue;
    prevIsAuthenticatedRef.current = currentValue;

    // If shift modal is showing, don't do anything - wait for user to select shift
    if (showShiftModal) {
      return;
    }

    // If other modals are showing, don't redirect
    if (showContinueModal || showCloseModal) {
      return;
    }

    // PRIORITY 1: Packaging station skips shift selection - go directly to /stacja-pakowania
    if (justLoggedIn && isPackaging) {
      navigate('/stacja-pakowania');
      return;
    }

    // Tracker to konto podgladowe — nic nie produkuje, wiec pytanie o zmiane jest bez sensu.
    if (justLoggedIn && isTracker) {
      navigate('/production-lines');
      return;
    }

    // PRIORITY 2: ALWAYS show shift selection modal after login (ask every time)
    // Show modal immediately when user just logged in
    if (justLoggedIn) {
      setShowShiftModal(true);
      return;
    }

    // PRIORITY 2: Check activeItem - if it exists, show modal immediately
    if (activeItem) {
      setShowContinueModal(true);
      return;
    }

    // PRIORITY 3: No active item, proceed to main page after a short delay
    const timer = setTimeout(() => {
      navigate('/');
    }, 100);

    return () => clearTimeout(timer);
  }, [isAuthenticated, isPackaging, isTracker, activeItem, showContinueModal, showCloseModal, showShiftModal, loading, navigate]);

  const handleContinueActiveItem = async () => {
    if (!activeItem) return;
    
    try {
      await api.post('/continue-active-item', {
        assignment_id: activeItem.assignment_id,
        item_id: activeItem.item_id,
      });
      
      setActiveItem(null);
      setShowContinueModal(false);
      navigate('/');
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || t('activeItem.couldNotContinue')}`);
    }
  };

  const handleCancelContinue = () => {
    setShowContinueModal(false);
    setShowCloseModal(true);
  };

  const handleCloseActiveItem = async (
    reason: 'niepelna_produkcja' | 'problem_bb' | 'problem_awaria' | 'pending',
    wykonanoIlosc?: number
  ) => {
    if (!activeItem) return;
    if (!selectedShift && reason === 'niepelna_produkcja') {
      alert(t('shift.mustSelectShift'));
      return;
    }
    
    try {
      const payload: any = {
        assignment_id: activeItem.assignment_id,
        item_id: activeItem.item_id,
        reason,
      };
      
      if (reason === 'niepelna_produkcja' && wykonanoIlosc !== undefined) {
        payload.wykonano_ilosc = wykonanoIlosc;
        if (selectedShift) {
          payload.shift = selectedShift;
        }
      }
      
      await api.post('/close-active-item', payload);
      
      setActiveItem(null);
      setShowCloseModal(false);
      navigate('/');
    } catch (err: any) {
      alert(`${t('common.error')}: ${err.response?.data?.message || t('activeItem.couldNotClose')}`);
    }
  };

  const handleCancelClose = () => {
    // User cancelled closing - go back to continue modal
    setShowCloseModal(false);
    setShowContinueModal(true);
  };

  const handleShiftSelect = (shift: 'zmiana_1' | 'zmiana_2') => {
    setSelectedShift(shift);
    setShowShiftModal(false);
    
    // After selecting shift, useEffect will handle the rest
    // It will check for activeItem and show modal or navigate accordingly
  };

  return (
    <div style={{ maxWidth: '450px', margin: '50px auto', padding: '30px', background: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '15px' }}>
        <LanguageSelector />
      </div>
      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#667eea' }}>🔐 {t('auth.login')}</h2>
      
      {loginMode === 'pin' && (
        <div style={{ marginBottom: '20px' }}>
          {selectedLine ? (
            // Line is already in localStorage/cookie - show it, don't allow change
            <div style={{
              padding: '15px',
              backgroundColor: '#e7f3ff',
              borderRadius: '8px',
              border: '1px solid #667eea'
            }}>
              <div style={{ fontSize: '14px', color: '#4a5568', marginBottom: '5px' }}>
                {t('auth.loggedInLine')}:
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#667eea' }}>
                🏭 {selectedLine.name}
              </div>
            </div>
          ) : (
            // No line in localStorage - show dropdown to select
            <>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                {t('auth.selectLine')}:
              </label>
              <select
                value={selectedLineId || ''}
                onChange={(e) => setSelectedLineId(Number(e.target.value))}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e1e8ed',
                  borderRadius: '8px',
                  fontSize: '16px',
                  backgroundColor: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {Array.isArray(availableLines) && availableLines.map((line) => (
                  <option key={line.id} value={line.id}>
                    {line.name} {line.description ? `- ${line.description}` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e1e8ed' }}>
        <button
          type="button"
          onClick={() => { setLoginMode('pin'); setError(''); setPin(''); }}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            background: loginMode === 'pin' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
            color: loginMode === 'pin' ? 'white' : '#667eea',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: loginMode === 'pin' ? '600' : '400',
          }}
        >
          {t('auth.pinDigits')}
        </button>
        <button
          type="button"
          onClick={() => { setLoginMode('email'); setError(''); }}
          style={{
            flex: 1,
            padding: '10px',
            border: 'none',
            background: loginMode === 'email' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
            color: loginMode === 'email' ? 'white' : '#667eea',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: loginMode === 'email' ? '600' : '400',
          }}
        >
          {t('auth.emailPassword')}
        </button>
      </div>

      {error && (
        <div style={{ 
          color: '#dc3545', 
          marginBottom: '15px', 
          padding: '12px', 
          backgroundColor: '#f8d7da', 
          borderRadius: '8px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {loginMode === 'pin' ? (
        <form onSubmit={handlePinSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
              {t('auth.enterPin')}:
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={pin}
              onChange={(e) => handlePinChange(e.target.value)}
              onInput={(e) => handlePinChange((e.target as HTMLInputElement).value)}
              placeholder="000000"
              maxLength={6}
              required
              autoFocus
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '24px',
                letterSpacing: '8px',
                textAlign: 'center',
                border: '2px solid #e1e8ed',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontWeight: '600',
              }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#718096', textAlign: 'center' }}>
              {t('auth.digitsCount', { count: pin.length.toString() })}
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || pin.length !== 6}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '16px',
              opacity: (loading || pin.length !== 6) ? 0.6 : 1,
              cursor: (loading || pin.length !== 6) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? t('auth.loggingIn') : t('auth.loginWithPin')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleEmailSubmit}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>{t('auth.email')}:</label>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              required
              className="input"
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>{t('auth.password')}:</label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              required
              className="input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{ width: '100%', padding: '14px', fontSize: '16px' }}
          >
            {loading ? t('auth.loggingIn') : t('auth.loginButton')}
          </button>
        </form>
      )}


      <p style={{ marginTop: '20px', textAlign: 'center', color: '#718096', fontSize: '14px' }}>
        {t('auth.noAccount')} <a href="/register" style={{ color: '#667eea', textDecoration: 'none' }}>{t('auth.register')}</a>
      </p>

      {showContinueModal && activeItem && (
        <ContinueActiveItemModal
          activeItem={activeItem}
          onContinue={handleContinueActiveItem}
          onCancel={handleCancelContinue}
        />
      )}

      {showCloseModal && activeItem && (
        <CloseActiveItemModal
          activeItem={activeItem}
          onClose={handleCloseActiveItem}
          onCancel={handleCancelClose}
        />
      )}

      {showShiftModal && (
        <ShiftSelectionModal onSelect={handleShiftSelect} />
      )}
    </div>
  );
};

export default Login;

